import type { ReactNode } from 'react';
import { createRoute, redirect, type AnyRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { listSearchSchema } from '@eis/core';
import { claimsPermissions as p } from '../permissions';
import { ClaimListPage } from '../features/claims/ui/ClaimListPage';
import { ClaimDetailPage } from '../features/claims/ui/ClaimDetailPage';
import { ClaimCreatePage } from '../features/claims/ui/ClaimCreatePage';

/**
 * The claims route tree (SDD-018 §9, C-12): `/claims`, `/claims/new`, `/claims/$code` — the
 * legacy suite's paths, so the later switch to full handling is a link change
 * (modules/claims/PERMISSIONS.md "Route groups").
 *
 * Permissions are declared ONCE here in `staticData`; `beforeLoad` enforces them and the
 * manifest's nav is filtered from the same declaration (Addendum A.2). `/claims/new` takes what a
 * policy page hands over through `links.claims.newClaim(...)`: the policy and, when known, the
 * damage date — Zod-validated, a malformed date is dropped rather than failing the route.
 *
 * The shell composes module route trees at runtime, so the parent is `AnyRoute` and param/search
 * inference cannot reach these components; the schemas are the runtime guarantee.
 */
const newClaimSearchSchema = z.object({
  policyCode: z.string().optional().catch(undefined),
  damageDate: z.iso.date().optional().catch(undefined),
});
type NewClaimSearch = z.infer<typeof newClaimSearchSchema>;
interface ClaimParams {
  readonly code: string;
}

interface GuardContext {
  readonly permissions: { canGroups: (groups: readonly (readonly string[])[]) => boolean };
}

const guard =
  (groups: readonly (readonly string[])[]) =>
  ({ context }: { context: GuardContext }): void => {
    if (!context.permissions.canGroups(groups)) {
      // TanStack signals navigation by throwing this sentinel — control flow, not an error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/403' });
    }
  };

export function createRoutes(parentRoute: AnyRoute): AnyRoute[] {
  const claimsIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/',
    validateSearch: listSearchSchema,
    staticData: { permissions: [[p.claims.view]] },
    beforeLoad: guard([[p.claims.view]]),
    component: ClaimListPage,
  });

  const newClaim = createRoute({
    getParentRoute: () => parentRoute,
    path: '/new',
    validateSearch: newClaimSearchSchema,
    staticData: { permissions: [[p.claims.create]] },
    beforeLoad: guard([[p.claims.create]]),
    component: function NewClaimRoute(): ReactNode {
      const { policyCode, damageDate } = newClaim.useSearch() as unknown as NewClaimSearch;
      return (
        <ClaimCreatePage
          key={`${policyCode ?? ''}:${damageDate ?? ''}`}
          initialPolicyCode={policyCode ?? ''}
          initialDamageDate={damageDate ?? ''}
        />
      );
    },
  });

  const claimDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/$code',
    staticData: { permissions: [[p.claims.view]] },
    beforeLoad: guard([[p.claims.view]]),
    component: function ClaimDetailRoute(): ReactNode {
      const { code } = claimDetail.useParams() as unknown as ClaimParams;
      return <ClaimDetailPage code={code} />;
    },
  });

  return [claimsIndex, newClaim, claimDetail];
}
